import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { Spinner } from '../ui/Spinner';
import DocumentReviewSurface from '../shared/DocumentReviewSurface';
import { EmptyState } from '../ui/EmptyState';
import { computeSelectionTotal, lineWhere, lineName, alternatePricingLabel, fmtSigned } from '../shared/QuoteSelectionPanel';
import { isOptionLine } from '../../quote-builder/separateOptions';
import computeSigningTotals from './computeSigningTotals';
import ChangeOrderSignView from './ChangeOrderSignView';
import { sanitizeTcHtml } from '../../utils/sanitizeTcHtml';
import { projectAlternatesFlat, deriveInitialSelections } from '../../quote-builder/selectionAdapter';
import api from '../../services/api';
import biditLogo from '../../assets/bidit-logo.svg';

// IDI-67: customer-facing branding allow-list from getSignaturePage's
// `branding` group. Keep this list in sync with brandingAllowList in
// cmd/signature/main.go — those are the ONLY fields this page may render.

// IDI-798: the signature handler's own not-found message (response.Error).
const HANDLER_NOT_FOUND = 'Document not found or link expired';

/**
 * SignaturePage — Customer-facing page for reviewing + accepting/declining a
 * quote or change order via a unique token link (unauthenticated).
 *
 * PT-37 (2026-07-28): extended to support:
 *   - DocumentReviewSurface with alternate/option selections (36.3/36.4)
 *   - Signature capture (canvas-based, no external dep)
 *   - accept-with-selections payload (B1 acknowledgeSignature)
 *   - document_type branching (B3 typed token: quote vs change_order)
 *   - 409 total_mismatch graceful handling (E1-Client)
 *   - Responsive + expired/invalid/already-accepted states
 *   - No cost or margin data exposed (36.7 / 30.5)
 */
export default function SignaturePage() {
  const { token } = useParams();

  // Data state
  const [document, setDocument] = useState(null);
  // IDI-370: true when `error` came from Accept/Decline, so it renders by the
  // buttons the customer just clicked rather than at the top of the page.
  const [actionError, setActionError] = useState(false);
  const [documentType, setDocumentType] = useState('quote'); // B3 discriminator
  const [signatureStatus, setSignatureStatus] = useState('');
  // IDI-67: dealer branding (logo_url, primary_color, secondary_color,
  // text_color, logo_position, company_name) from getSignaturePage's
  // `branding` group. null until loaded; the header falls back to the BIDIT
  // logo only if the dealer has no logo_url set (never as the default look).
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Selection state (PT-36 / B1)
  const [selections, setSelections] = useState({ alternates: [], options: [] });
  const [computedTotal, setComputedTotal] = useState(0);
  // Expanded (flat) line items: alternates promoted to siblings, options tagged.
  // This is the model QuoteSelectionPanel expects.
  const [expandedItems, setExpandedItems] = useState([]);
  // IDI-32: true when neither line items nor a stored total exist, i.e. we cannot
  // show the customer a price we can stand behind. Signing is blocked in that
  // case rather than letting them accept an unpriced document.
  const [unpriceable, setUnpriceable] = useState(false);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [result, setResult] = useState(null); // 'acknowledged' | 'declined'
  // const [acceptedConfigId, setAcceptedConfigId] = useState(null); // PT-37: available for downstream if needed
  const [lockedTotal, setLockedTotal] = useState(null);

  // Decline state
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  // Signature state
  const [hasSignature, setHasSignature] = useState(false);
  // IDI-33: the exported PNG data URL. Previously the drawing was discarded and
  // only `hasSignature` (a boolean) was kept, so nothing recorded WHAT was signed.
  const [signatureImage, setSignatureImage] = useState(null);
  const [smsOptIn, setSmsOptIn] = useState(false);

  // ─── Load document via token ─────────────────────────────────────────────
  const loadDocument = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getSignaturePage(token);
      const data = res?.data || res;
      if (!data?.quote) {
        setError('Document not found or link expired.');
        return false;
      }
      setDocument(data.quote);
      setDocumentType(data.document_type || 'quote');
      setSignatureStatus(data.signature_status || '');
      // IDI-67: dealer branding — present on both the active and
      // already-actioned response shapes. null/absent degrades to the BIDIT
      // fallback logo (never a broken image).
      setBranding(data.branding || null);

      // Seed selections from document's alternates/options.
      // projectAlternatesFlat converts the nested alternate model (line.alternates[])
      // into the flat sibling model that QuoteSelectionPanel expects.
      const lineItems = data.quote?.line_items || data.quote?.lineItems || [];
      const expanded = projectAlternatesFlat(lineItems);
      setExpandedItems(expanded);
      const initialSelections = deriveInitialSelections(expanded);
      setSelections(initialSelections);

      // Compute initial total respecting default selections (options opt-in,
      // parent of alternate group pre-selected per ruling 3k).
      const total = computeSelectionTotal(expanded, initialSelections);
      setComputedTotal(total);

      // IDI-32: a document with no line items cannot be priced client-side, so
      // `total` is 0 — which previously rendered as a confident "$0.00" right
      // next to Accept & Sign. DocumentReviewSurface now suppresses the total row
      // and shows "contact the sender", but suppressing the PRICE while leaving
      // the SIGN button live would still let a customer accept a quote whose
      // value they were never shown. Block signing too.
      //
      // Uses the stored total as the signal, matching DocumentReviewSurface:
      // bidAmountCents is the real field on a BIDIT quote (45,326 of 46,974
      // populated); there is no total_amount / total_cents on the record.
      // IDI-42: unpriceable = no line items. If there are no line items,
      // derivation yields 0 and the server refuses (409 total_unavailable)
      // regardless of what is stored. The hasStoredTotal refinement made the
      // predicate LESS correct: it enabled Accept & Sign for migrated quotes
      // that the server always rejects.
      // IDI-452 (B-05/C-02): a change order is signable when the server says
      // so (it has lines and its tax is known). A $0 or credit CO is fine.
      setUnpriceable(data.quote?.kind === 'change_order' ? data.quote.signable === false : lineItems.length === 0);
      return true;
    } catch (err) {
      if (err?.response?.status === 410) {
        setError('This signature link has expired.');
      } else if (err?.response?.status === 404) {
        setError('Document not found or link expired.');
      } else {
        setError('Failed to load document. Please try again.');
      }
      return false;
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadDocument(); }, [loadDocument]);

  // IDI-370: subtotal -> discounts -> tax, identical to the server's
  // DeriveAcceptedTotal. `computedTotal` stays the selections subtotal.
  const totals = useMemo(() => computeSigningTotals(computedTotal, document), [computedTotal, document]);

  // ─── Selection change handler (36.5: live total recomputes) ──────────────
  const handleSelectionChange = useCallback((newSelections) => {
    setSelections(newSelections);
    // Recompute total using the flat model — respects alternate choice +
    // option opt-in/out per computeSelectionTotal's logic.
    setComputedTotal(computeSelectionTotal(expandedItems, newSelections));
  }, [expandedItems]);

  // ─── Accept (B1: acknowledgeSignature with selections + checksum) ────────
  // IDI-370: errors raised by Accept / Decline render beside those buttons.
  const setActionErr = useCallback((msg) => {
    setActionError(msg != null);
    setError(msg);
  }, []);

  // IDI-778: a link that closed or expired while the page was open shows the
  // closed-link card (it renders without a document).
  const showLinkClosed = useCallback((msg = 'This signature link has expired.') => {
    setActionError(false);
    setError(msg);
    setDocument(null);
  }, []);

  // IDI-798: a Reopen or a new link retires this token; the handler answers
  // 404 with response.Error's own body ({success:false,message}). Only that
  // exact body shows the card; any other 404 keeps "Please try again".
  const isHandlerNotFound = (err) =>
    err?.response?.status === 404 && err?.response?.data?.message === HANDLER_NOT_FOUND;

  const handleAccept = async () => {
    if (!hasSignature) {
      setActionErr('Please sign above before accepting.');
      return;
    }
    try {
      setActionLoading(true);
      setActionErr(null);
      const payload = {
        token,
        sms_opt_in: smsOptIn,
        channel: 'e_sign',
        // IDI-33: send the drawing and the signer's identity so the acceptance
        // is evidenced, not merely asserted. The backend stores the image in S3
        // and records source IP + user-agent from the request itself (a
        // client-supplied IP would be worthless as evidence).
        signature_image: signatureImage,
        signer_email: document?.signatureSentTo || undefined,
        // IDI-452: a change order has no selections; its total is locked
        // server-side from the same projection the page shows.
        ...(document?.kind === 'change_order' ? {} : {
          selections: {
            alternates: selections.alternates,
            options: selections.options,
            // IDI-370: the TAXED, DISCOUNTED total the server verifies, not
            // the bare selections subtotal (every taxed quote 409'd).
            client_total_cents: totals.total,
          },
        }),
      };
      const res = await api.acknowledgeQuote(token, payload);
      const data = res?.data || res;
      setResult('acknowledged');
      // setAcceptedConfigId(data?.accepted_configuration_id); // available if needed downstream
      setLockedTotal(data?.locked_total_cents);
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data || err?.data;
      if (status === 409 && body?.error === 'total_mismatch') {
        // E1-Client: re-fetch, re-render server's figures, ask to confirm again.
        // IDI-370: reload FIRST. loadDocument opens with setError(null), so
        // setting the message before it wiped the message — the customer saw
        // "nothing happen" and the quote stayed Pending. If the reload itself
        // fails, its own load error stays on screen instead.
        const reloaded = await loadDocument(); // re-fetch with server's current state
        if (reloaded) {
          setActionErr(
            'The quote total has been updated. Please review the updated figures and confirm again.'
          );
        }
      } else if (status === 410) {
        showLinkClosed();
      } else if (isHandlerNotFound(err)) {
        showLinkClosed('Document not found or link expired.');
      } else if (status === 409 && body?.error === 'already_actioned') {
        setResult('acknowledged');
      } else if (status === 409 && (body?.error === 'tax_unknown' || body?.error === 'no_changes')) {
        // IDI-452 (C-02): a change order that can't be signed yet.
        setActionErr(body?.message || 'This change order can\u2019t be signed yet. Please contact your contractor.');
      } else if (status === 409 && body?.error === 'total_unavailable') {
        // IDI-36 backstop: the server refused to lock a $0 total on a quote that
        // has a real stored total (line items missing, so unverifiable). IDI-32
        // should keep this document unsignable on the client already; this gives
        // the customer the accurate reason rather than the generic failure.
        setActionErr(
          body?.message ||
            'This quote\u2019s total could not be verified. Please contact the sender before signing.'
        );
      } else {
        setActionErr('Failed to accept. Please try again.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Decline ─────────────────────────────────────────────────────────────
  const handleDecline = async () => {
    try {
      setActionLoading(true);
      setActionErr(null);
      await api.declineQuote(token, declineReason);
      setResult('declined');
    } catch (err) {
      if (err?.response?.status === 410) {
        showLinkClosed();
      } else if (isHandlerNotFound(err)) {
        showLinkClosed('Document not found or link expired.');
      } else {
        setActionErr('Failed to decline. Please try again.');
      }
    } finally {
      setActionLoading(false);
    }
  };

  // ─── Render states ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
        <Spinner />
      </div>
    );
  }

  // Already actioned (loaded in acknowledged/declined state)
  const isAlreadyActioned = signatureStatus === 'acknowledged' || signatureStatus === 'declined';

  if (result || isAlreadyActioned) {
    const isAck = result === 'acknowledged' || signatureStatus === 'acknowledged';
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircleIcon className="h-16 w-16 mx-auto mb-4" style={{ color: isAck ? 'var(--color-success)' : 'var(--color-text-muted)' }} />
          <h1 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            {isAck ? `${documentType === 'change_order' ? 'Change Order' : 'Quote'} Accepted` : `${documentType === 'change_order' ? 'Change Order' : 'Quote'} Declined`}
          </h1>
          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
            {isAck
              ? 'Thank you! Your acceptance has been recorded.'
              : 'The document has been declined. The contractor has been notified.'}
          </p>
          {lockedTotal != null && (
            <p className="mt-4 text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Total: ${((lockedTotal || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
            </p>
          )}
        </Card>
      </div>
    );
  }

  if (error && !document) {
    // PR-36 §2.1 (2026-08-17), amended per 328_ §4: the fatal-error card
    // used to render a bare white card with a red X and the raw error text.
    // No identity, no route forward for a customer whose link expired. Now:
    //
    //   * Heading that acknowledges the specific failure category.
    //   * The error message stays (it distinguishes expired / not-found).
    //   * Recourse copy: contact whoever sent you this link.
    //
    // 🛑 No logo above the fold. Portica 328_ §4: a homeowner expecting
    //    "1st Choice Insulation" seeing a BIDIT mark on a broken-link page
    //    reads as a wrong link, not as reassurance. Platform-identity, if
    //    it wants to live here, belongs as a quiet footer per Adrian's
    //    ruling.
    //
    // 🛑 Contractor branding on this card requires IDI-67. The signature
    //    endpoint is unauthenticated and does not project tenant identity;
    //    a backend field group closes that, and then this card can render
    //    the CONTRACTOR's identity + phone. Until then, the recourse copy
    //    IS the route forward and the card stays clean.
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
        <Card className="max-w-md w-full p-8 text-center">
          <XMarkIcon className="h-12 w-12 mx-auto mb-4 text-red-500" />
          <h1 className="text-lg font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            This link is no longer available
          </h1>
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>{error}</p>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Please contact the person who sent you this link to request a new one.
          </p>
        </Card>
      </div>
    );
  }

  const lineItems = document?.line_items || document?.lineItems || [];
  // IDI-362 / IDI-131 (10-02 ruling, D-08): every non-option line is listed
  // here. A line with alternates is listed too, and each of its alternates
  // renders DIRECTLY UNDER it as a clearly labelled choice ("Alternate pricing
  // — <area> · <sub-area>", product, signed difference). Options stay in their
  // own labelled section (QuoteSelectionPanel). Plain derivation, not a hook:
  // this runs after the early returns above.
  const includedLines = lineItems.filter((li) => !isOptionLine(li));
  const chosenFor = (groupId) =>
    (selections.alternates || []).find((a) => a.group_id === groupId)?.chosen_line_id ?? groupId;
  const chooseAlternate = (groupId, lineId) => {
    handleSelectionChange({
      ...selections,
      alternates: (selections.alternates || []).map((a) =>
        a.group_id === groupId ? { ...a, chosen_line_id: lineId } : a
      ),
    });
  };
  // §3: display toggles persisted at Send — honour them on the customer page.
  // Defaults match DISPLAY_TOGGLES in QuoteReviewSend (all on except laborPricing).
  const displayOpts = document?.displayOptions || {};
  const showLineItemPricing = displayOpts.lineItemPricing !== false;
  // showNotes reserved for when a notes section is added to the signature page.
  // const showNotes = displayOpts.notes !== false;
  // IDI-812 D1-D3: the terms acceptance freezes (server-resolved), cleaned as
  // everywhere else (IDI-624). Hidden when nothing visible remains.
  const rawTerms = typeof document?.terms === 'string' ? document.terms : '';
  const cleanTerms = sanitizeTcHtml(rawTerms);
  const termsArePlain = !/<[a-z][\s\S]*>/i.test(rawTerms);

  return (
    <div className="min-h-screen p-4 md:p-8" style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        {/* IDI-67: the customer must see the DEALER's identity, not BIDIT's.
            branding.logo_url (from getSignaturePage's allow-listed branding
            group) takes priority; the BIDIT mark is only a fallback for
            dealers who haven't set a logo, never the default look. */}
        <div className="flex items-center gap-3">
          <img
            src={branding?.logo_url || biditLogo}
            alt={branding?.company_name || 'BIDIT'}
            className="h-8"
            onError={(e) => { e.currentTarget.src = biditLogo; }}
          />
          <div>
            <h1 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {documentType === 'change_order' ? 'Change Order Review' : 'Quote Review'}
            </h1>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              {branding?.company_name
                ? `${branding.company_name} — please review the details below and sign to accept.`
                : 'Please review the details below and sign to accept.'}
            </p>
          </div>
        </div>

        {/* Error banner (non-fatal — document still shown). IDI-370: accept /
            decline errors render next to the buttons instead (below). */}
        {error && !actionError && (
          <Alert variant="warning" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Document Review Surface (PT-36).
            DEF-088 (W1.1c): customerView=true — SignaturePage is the
            unauthenticated customer-facing token path. DRS renders the
            softer customer-tone notice and suppresses the $ total row
            entirely when line items are missing (response §5 hard rule). */}
        {/* IDI-452 v2: a change order renders from the server's CO projection
            (lines, net, tax, total), not the quote surface. */}
        {document?.kind === 'change_order' ? (
          <Card><ChangeOrderSignView doc={document} /></Card>
        ) : (
        <DocumentReviewSurface
          documentType={documentType}
          document={document}
          selections={selections}
          onSelectionChange={handleSelectionChange}
          readOnly={false}
          lineItems={expandedItems}
          computedTotal={totals.total}
          totalsBreakdown={totals}
          customerView={true}
          hideAlternates
        >
          {/* The rendered document preview — simplified for the signature page.
              A full QuoteDocument render requires branding/profile context that
              isn't available on the unauthenticated token path. Show key fields. */}
          <div className="p-6 space-y-4">
            <h2 className="text-base font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {document?.title || document?.quote_number || 'Document'}
            </h2>
            {document?.customer_name && (
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                Prepared for: {document.customer_name}
              </p>
            )}
            {/* Line items summary (no cost/margin — 36.7 / 30.5).
                DEF-094 (W1.1d): the previous silent-hide
                `{lineItems.length > 0 && (...)}` swallowed the empty case —
                a customer arriving at a signature link for a document with
                no line-item detail saw a blank body and no indication
                anything was wrong. Replace with canonical EmptyState +
                customer-tone copy. DRS's customer-tone missing-lines Alert
                (W1.1c) provides the actionable "contact the sender" hint;
                this EmptyState is the softer in-summary acknowledgement. */}
            <div
              className="border-t pt-4"
              style={{ borderColor: 'var(--color-border)' }}
              data-testid="signature-page-line-items-summary"
            >
              {lineItems.length > 0 ? (
                // IDI-362: only non-option lines, each labelled "Area — Sub-area"
                // + alias || product (+ note). IDI-131: each alternate sits
                // directly under its parent line as a radio choice; picking it
                // changes the total via computeSelectionTotal, as before.
                includedLines.map((li) => {
                  const alts = Array.isArray(li.alternates) ? li.alternates : [];
                  const hasAlts = alts.length > 0;
                  const parentTotal = li.line_total ?? li.lineTotal ?? 0;
                  const chosen = hasAlts ? chosenFor(li.id) : null;
                  return (
                    <div key={li.id} data-testid="signature-summary-group">
                      <div className="flex justify-between gap-3 py-1 text-sm" data-testid="signature-summary-line">
                        <span className="min-w-0 flex items-start gap-2">
                          {hasAlts && (
                            <input
                              type="radio"
                              className="mt-1"
                              name={`sig-alt-${li.id}`}
                              aria-label={`As quoted: ${lineName(li)}`}
                              checked={chosen === li.id}
                              onChange={() => chooseAlternate(li.id, li.id)}
                              style={{ accentColor: 'var(--color-action-primary)' }}
                            />
                          )}
                          <span className="min-w-0">
                            {lineWhere(li) && (
                              <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }}>{lineWhere(li)}</span>
                            )}
                            <span style={{ color: 'var(--color-text-default)' }}>{lineName(li)}</span>
                            {li.notes && (
                              <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }}>{li.notes}</span>
                            )}
                          </span>
                        </span>
                        {showLineItemPricing && (
                          <span className="tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                            ${(parentTotal / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                          </span>
                        )}
                      </div>
                      {alts.map((alt) => {
                        const altTotal = alt.total ?? alt.lineTotal ?? alt.line_total ?? 0;
                        return (
                          <label
                            key={alt.id}
                            className="flex justify-between gap-3 py-1 pl-6 text-sm cursor-pointer"
                            data-testid="signature-alternate-row"
                            data-parent-id={li.id}
                          >
                            <span className="min-w-0 flex items-start gap-2">
                              <input
                                type="radio"
                                className="mt-1"
                                name={`sig-alt-${li.id}`}
                                checked={chosen === alt.id}
                                onChange={() => chooseAlternate(li.id, alt.id)}
                                style={{ accentColor: 'var(--color-action-primary)' }}
                              />
                              <span className="min-w-0">
                                <span className="text-xs block" style={{ color: 'var(--color-text-muted)' }} data-testid="signature-alternate-label">
                                  {alternatePricingLabel(alt, li)}
                                </span>
                                <span style={{ color: 'var(--color-text-default)' }}>
                                  {(typeof alt.alias === 'string' && alt.alias.trim()) || alt.productLabel || alt.product_label || 'Alternate'}
                                </span>
                              </span>
                            </span>
                            {showLineItemPricing && (
                              <span className="tabular-nums" style={{ color: 'var(--color-text-primary)' }} data-testid="signature-alternate-delta">
                                {fmtSigned(altTotal - parentTotal)}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  );
                })
              ) : (
                <div data-testid="signature-page-line-items-empty">
                  <EmptyState
                    mode="first-run"
                    title="Line-item detail is not available for this document."
                  />
                </div>
              )}
            </div>
          </div>
        </DocumentReviewSurface>
        )}

        {/* IDI-812 D2: the quote's terms, full length, before the signature pad. */}
        {document?.kind !== 'change_order' && cleanTerms && (
          <Card className="p-4" data-testid="signature-page-terms">
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
              Terms &amp; conditions
            </h3>
            <div
              className={`text-sm${termsArePlain ? ' whitespace-pre-wrap' : ''}`}
              style={{ color: 'var(--color-text-default)' }}
              data-testid="signature-page-terms-body"
              dangerouslySetInnerHTML={{ __html: cleanTerms }}
            />
          </Card>
        )}

        {/* Signature Pad */}
        <Card className="p-4">
          <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
            Signature
          </h3>
          <SignaturePad
            onSign={() => setHasSignature(true)}
            onClear={() => { setHasSignature(false); setSignatureImage(null); }}
            onCapture={setSignatureImage}
          />
        </Card>

        {/* SMS opt-in */}
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={smsOptIn}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            style={{ accentColor: 'var(--color-action-primary)' }}
          />
          <span style={{ color: 'var(--color-text-default)' }}>
            I agree to receive SMS notifications about this project
          </span>
        </label>

        {/* IDI-32: an unpriceable document must not be signable. Previously the
            page rendered "$0.00" and left Accept & Sign enabled, so a customer
            could accept a quote worth $26,094 believing it was free — or accept
            one whose value was never shown to them at all. Decline stays
            available: refusing an incomplete document is legitimate. */}
        {unpriceable && (
          <Alert variant="warning">
            {document?.kind === 'change_order'
              ? 'This change order can\u2019t be signed yet: its tax couldn\u2019t be determined, or it has no changes. Please contact your contractor.'
              : 'This document does not show a price yet, so it cannot be signed. Please contact the sender for an updated quote.'}
          </Alert>
        )}

        {/* Action buttons */}
        {/* IDI-370: a refusal (total_mismatch, zero-total, network) must be
            visible WHERE the customer clicked. The only error banner was at
            the top of a long page, so an Accept failure looked like nothing
            happened. role=alert so it's announced too. */}
        {error && actionError && (
          <div role="alert" data-testid="signature-accept-error">
            <Alert variant="warning" onClose={() => setError(null)}>
              {error}
            </Alert>
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handleAccept}
            disabled={actionLoading || !hasSignature || unpriceable}
            className="flex-1"
          >
            {actionLoading ? 'Processing\u2026' : 'Accept & Sign'}
          </Button>
          {!showDeclineForm ? (
            <Button
              variant="ghost"
              onClick={() => setShowDeclineForm(true)}
              disabled={actionLoading}
            >
              Decline
            </Button>
          ) : (
            <div className="flex-1 space-y-2">
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder={'Reason for declining (optional)…'}
                className="w-full text-sm border rounded-md p-2"
                style={{ borderColor: 'var(--color-border)', minHeight: '60px' }}
                rows={2}
              />
              <div className="flex gap-2">
                <Button variant="danger" size="sm" onClick={handleDecline} disabled={actionLoading}>
                  Confirm Decline
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowDeclineForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SignaturePad (canvas-based, no external dep) ────────────────────────────

function SignaturePad({ onSign, onClear, onCapture }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    if (!hasContent) {
      setHasContent(true);
      onSign?.();
    }
  };

  const endDraw = () => {
    setIsDrawing(false);
    // IDI-33: export the drawing on stroke end so the parent holds the actual
    // image, not just a boolean. Previously the canvas was discarded entirely —
    // the customer drew a signature, saw it, submitted, and nothing was kept.
    // Exported here rather than on every mousemove: toDataURL is comparatively
    // expensive and the value is only needed at submit time.
    if (canvasRef.current && hasContent) {
      try {
        onCapture?.(canvasRef.current.toDataURL('image/png'));
      } catch {
        // A tainted canvas cannot be exported. Acknowledgement must still be
        // possible, so swallow and let the backend record `not_provided`.
        onCapture?.(null);
      }
    }
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    // clearRect works in the SCALED coordinate space, so use CSS dimensions here,
    // not canvas.width/height (which are buffer pixels = CSS x dpr).
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    setHasContent(false);
    onClear?.();
  };

  // The stroke did not follow the cursor.
  //
  // The canvas had fixed width/height ATTRIBUTES (500x150 — its drawing buffer)
  // but is laid out with `w-full`, so CSS stretched it. Measured on the live page:
  //
  //   buffer 500x150   CSS 734x222   -> scale 0.681 / 0.677   dpr 2
  //
  // getBoundingClientRect() returns CSS pixels, so `clientX - rect.left` was in
  // CSS space while ctx drawing commands are in buffer space. Every point landed
  // at ~68% of where the cursor was — and the error grew with distance from the
  // top-left origin, which is why it looked like an offset rather than a scale.
  //
  // Fix: size the buffer to the element's REAL rendered size (x devicePixelRatio
  // so it stays crisp on retina), then scale the context by dpr. Drawing
  // coordinates are then CSS pixels and map 1:1 to rect-relative positions, so
  // getPos needs no per-point correction.
  //
  // Re-run on resize, because the buffer must track the element's width. Resizing
  // a canvas clears it, so the signature is reset and the parent is told —
  // silently keeping a "signed" state with a blank canvas would be worse.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const applySize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const dpr = window.devicePixelRatio || 1;

      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);

      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0); // reset before re-scaling
      ctx.scale(dpr, dpr);
      ctx.strokeStyle =
        getComputedStyle(canvas).getPropertyValue('--color-text-default').trim() || 'black';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };

    applySize();

    let raf = null;
    const ro = new ResizeObserver(() => {
      // Coalesce bursts of resize callbacks into one re-size per frame.
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        applySize();
        // Resizing wipes the canvas — don't leave a stale "captured" state.
        setHasContent(false);
        onClear?.();
      });
    });
    ro.observe(canvas);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
    };
    // onClear is intentionally excluded: it is a stable callback from the parent
    // and including it would tear down/rebuild the observer on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full border rounded-md cursor-crosshair"
        style={{
          borderColor: 'var(--color-border)',
          touchAction: 'none',
          // Height must come from CSS, not a width/height attribute. The effect
          // above derives the drawing buffer from the element's rendered size, so
          // any fixed attribute would re-introduce the buffer-vs-CSS mismatch that
          // made the stroke lag the cursor.
          height: '150px',
          display: 'block',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <div className="flex justify-between mt-2">
        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
          {hasContent ? 'Signature captured' : 'Sign above to accept'}
        </span>
        {hasContent && (
          <button
            type="button"
            onClick={clear}
            className="text-xs font-medium"
            style={{ color: 'var(--color-action-primary)' }}
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
